<template>
  <div :class="{ 'hidden lg:flex': compact }">
    <TButton
      v-bind="$attrs"
      type="button"
      tabindex="0"
      @click.prevent="handleClick"
    >
      <TIcon
        v-if="!hideIcon"
        :icon="['far', 'share-nodes']"
      /><span
        :class="hideIcon ? '' : 'ml-2'"
      >{{ buttonLabel }}</span>
    </TButton>
  </div>
  <div :class="{ 'block lg:hidden': compact, 'hidden': !compact }">
    <button
      class="size-8 rounded-full bg-white flex items-center justify-center cursor-pointer opacity-80 hover:opacity-100 transition"
      type="button"
      tabindex="0"
      @click.prevent="handleClick"
    >
      <TIcon
        v-if="!hideIcon"
        :icon="['far', 'share-nodes']"
      />
    </button>
  </div>

  <TPopover
    v-if="!isWebView"
    ref="sharePopoverEl"
    placement="bottom"
  >
    <template #default>
      <AppSocialShare
        :page="page"
        :url="url"
        :show-items-vertical="showItemsVertical"
      />
    </template>
  </TPopover>
</template>

<script setup lang="ts">
import type { ComponentInstance } from 'vue'
import type { TPopover } from '#components'
import { COOKIES } from '#shared/constants/cookies'

const props = withDefaults(
  defineProps<{
    hideIcon?: boolean
    showItemsVertical?: boolean
    page: string
    endUrl?: string
    showTextPromote?: boolean
    buttonText?: string
    // Temporary prop to deal with the RV details page being ported to Tailwind.
    // Eventually we need a proper component for this.
    compact?: boolean
  }>(),
  {
    endUrl: '',
    buttonText: '',
  },
)

defineOptions({
  inheritAttrs: false,
})

const { t } = useI18n()

const sharePopoverEl = ref<ComponentInstance<typeof TPopover>>()

const sessionIdCookie = useCookie(COOKIES.session)
const runtimeConfig = useRuntimeConfig()
const route = useRoute()
const { isWebView } = usePlatform()

const url = computed(() => {
  return props.endUrl !== ''
    ? `${runtimeConfig.public.baseURL}${props.endUrl}`
    : `${runtimeConfig.public.baseURL}${route.path}?rts=${sessionIdCookie.value}`
})

const buttonLabel = computed(() => {
  if (props.buttonText) {
    return props.buttonText
  }
  else if (props.showTextPromote) {
    return t('promote')
  }
  else {
    return t('actions.share')
  }
})

/**
 * Handles clicking the share button.
 *
 * When the user is using a webview (from the app), we post a message to the app
 * instead.
 *
 * @param event The mouse event that triggered the click
 */
function handleClick(event: MouseEvent) {
  if (!isWebView.value) {
    event.preventDefault()
    sharePopoverEl.value?.toggleShow(event.currentTarget as HTMLElement)
    return
  }

  // Shared links never carry the visitor's ad click IDs (RVZ-28582).
  const sharedUrl = stripClickIds(window.location.href)

  postMessageToMobileApp({
    android: { func: 'shareUrl', params: [sharedUrl] },
    ios: {
      command: 'shareUrl',
      parameters: {
        url: sharedUrl,
      },
    },
  })
}
</script>

<i18n lang="json">
{
  "en": {
    "promote": "Promote"
  },
  "fr": {
    "promote": "Promouvoir"
  }
}
</i18n>
